import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Button,
  Spinner,
  Chip,
  Switch,
  Select,
  SelectItem,
} from "@heroui/react";
import {
  Image as ImageIcon,
  Trash,
  Check,
  Plus,
  CaretLeft,
  CaretRight,
  Tag,
} from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import EntitySearchBar from "./EntitySearchBar";
import { Acquisition } from "shared/types/acquisition";

interface ImageTablePickerProps {
  selectedImageIds: string[];
  onImagesSelect: (imageIds: string[]) => void;
  buildingId?: string;
  unitId?: string;
  acquisitionId?: string;
}

interface FileLog {
  id: number;
  sharePointFileId: string;
  fileName: string;
  imageUrl?: string;
  imageFound: boolean;
}

interface FileLabel {
  id: string;
  label: string;
}

const ImageTablePicker: React.FC<ImageTablePickerProps> = ({
  selectedImageIds,
  onImagesSelect,
  buildingId,
  unitId,
  acquisitionId,
}) => {
  const { session } = useSession();
  const [images, setImages] = useState<FileLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalImages, setTotalImages] = useState(0);
  // deze 3 toegebvoegd
  const [useContextFilter, setUseContextFilter] = useState(true);
  const [availableLabels, setAvailableLabels] = useState<FileLabel[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>("");

  const pageSize = 12;

  // Entity selection state - overrides context when selected
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const [selectedAcquisitionId, setSelectedAcquisitionId] =
    useState<string>("");
  const [availableAcquisitions, setAvailableAcquisitions] = useState<
    Acquisition[]
  >([]);
  const [loadingAcquisitions, setLoadingAcquisitions] = useState(false);

  // Fetch available labels - met labels kan je filteren op de fetched images
  const fetchLabels = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/search-labels?term=&resourceTypes=building,unit&documentTypes=picture`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        session,
      );

      if (!response.ok) throw new Error("Failed to fetch labels");

      const data = await response.json();
      setAvailableLabels(data);
      console.log(`📋 Fetched ${data.length} file labels for image picker`);
    } catch (error) {
      console.error("❌ Error fetching file labels:", error);
    }
  }, [session]);

  // Fetch labels on component mount
  useEffect(() => {
    if (session) {
      fetchLabels();
    }
  }, [session, fetchLabels]);

  // Fetch available acquisitions
  const fetchAcquisitions = useCallback(async () => {
    try {
      setLoadingAcquisitions(true);
      const response = await fetchWithAuth(
        `${API_URL}/acquisitions`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        session,
      );

      if (!response.ok) throw new Error("Failed to fetch acquisitions");

      const data = await response.json();
      setAvailableAcquisitions(data);
      console.log(`📋 Fetched ${data.length} acquisitions for image picker`);
    } catch (error) {
      console.error("❌ Error fetching acquisitions:", error);
    } finally {
      setLoadingAcquisitions(false);
    }
  }, [session]);

  // Fetch acquisitions on component mount
  useEffect(() => {
    if (session) {
      fetchAcquisitions();
    }
  }, [session, fetchAcquisitions]);

  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);

      // Use selected entities first, then fall back to context if toggle is enabled
      const effectiveBuildingId =
        selectedBuilding?.resourceId ||
        selectedBuilding?.id ||
        (useContextFilter ? buildingId : "");
      const effectiveUnitId =
        selectedUnit?.resourceId ||
        selectedUnit?.id ||
        (useContextFilter ? unitId : "");
      const effectiveAcquisitionId =
        selectedAcquisitionId || (useContextFilter ? acquisitionId : "");

      console.log(`📸 ImageTablePicker fetching images:`, {
        useContextFilter,
        selectedBuildingId:
          selectedBuilding?.resourceId || selectedBuilding?.id,
        selectedUnitId: selectedUnit?.resourceId || selectedUnit?.id,
        selectedAcquisitionId,
        buildingId: effectiveBuildingId,
        unitId: effectiveUnitId,
        acquisitionId: effectiveAcquisitionId,
      });

      const queryParams = new URLSearchParams({
        buildingId: effectiveBuildingId || "",
        unitId: effectiveUnitId || "",
        acquisitionId: effectiveAcquisitionId || "",
        exclusionType: "",
        fileLabel: selectedLabel || "",
        secondaryLabel: "",
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await fetchWithAuth(
        `${API_URL}/file/get-images?${queryParams}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            labels: [],
            buildingId: effectiveBuildingId,
            unitId: effectiveUnitId,
            acquisitionId: effectiveAcquisitionId,
          }),
        },
        session,
      );

      if (!response.ok) throw new Error("Failed to fetch images");

      const data = await response.json();
      const filteredImages = data.filter((img: FileLog) => img.imageFound);
      console.log(
        `📊 ImageTablePicker found ${filteredImages.length} images (page ${currentPage})`,
      );
      setImages(filteredImages);

      // Estimate total based on page results
      if (filteredImages.length === pageSize) {
        setTotalImages(currentPage * pageSize + 1);
      } else {
        setTotalImages((currentPage - 1) * pageSize + filteredImages.length);
      }
    } catch (error) {
      console.error(`❌ Error fetching images for image table:`, error);
    } finally {
      setLoading(false);
    }
  }, [
    session,
    buildingId,
    unitId,
    acquisitionId,
    currentPage,
    useContextFilter,
    selectedLabel,
    selectedBuilding,
    selectedUnit,
    selectedAcquisitionId,
  ]);

  useEffect(() => {
    if (session && showPicker) {
      fetchImages();
    }
  }, [session, showPicker, fetchImages]);

  const handleImageClick = (sharePointFileId: string) => {
    if (selectedImageIds.includes(sharePointFileId)) {
      // Remove from selection
      onImagesSelect(selectedImageIds.filter((id) => id !== sharePointFileId));
    } else {
      // Add to selection
      onImagesSelect([...selectedImageIds, sharePointFileId]);
    }
  };

  const handleClearAll = () => {
    onImagesSelect([]);
  };

  const isSelected = (sharePointFileId: string) => {
    return selectedImageIds.includes(sharePointFileId);
  };

  return (
    <div className="space-y-3">
      {!showPicker ? (
        <Button
          color="default"
          variant="bordered"
          startContent={<Plus size={16} />}
          onPress={() => setShowPicker(true)}
          className="w-full"
        >
          Voeg foto tabel toe aan het einde van de pagina
        </Button>
      ) : (
        <Card>
          <CardBody className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ImageIcon size={20} className="text-purple-600" />
                <h6 className="text-neutral-primaryDark font-semibold">
                  Selecteer foto's voor tabel
                </h6>
              </div>
              <div className="flex items-center gap-2">
                <Chip size="sm" variant="flat" color="secondary">
                  {selectedImageIds.length} geselecteerd
                </Chip>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  startContent={<Trash size={14} />}
                  onPress={() => setShowPicker(false)}
                >
                  Sluiten
                </Button>
              </div>
            </div>

            {/*//Entity picker toevoegen*/}
            <div className="space-y-3">
              <EntitySearchBar
                entityType="building"
                entityInstance="imagePickerBuilding"
                displayName="Gebouw"
                selectedEntity={selectedBuilding}
                onEntitySelect={(building) => {
                  setSelectedBuilding(building);
                  setCurrentPage(1);
                }}
                onEntityClear={() => {
                  setSelectedBuilding(null);
                  setCurrentPage(1);
                }}
              />

              <EntitySearchBar
                entityType="unit"
                entityInstance="imagePickerUnit"
                displayName="Unit"
                selectedEntity={selectedUnit}
                onEntitySelect={(unit) => {
                  setSelectedUnit(unit);
                  setCurrentPage(1);
                }}
                onEntityClear={() => {
                  setSelectedUnit(null);
                  setCurrentPage(1);
                }}
              />

              {/* Acquisition Selector */}
              <div className="mb-4 space-y-2">
                <label className="text-neutral-primaryDark block text-sm font-medium">
                  Acquisitie
                </label>
                <Select
                  size="sm"
                  placeholder="Selecteer acquisitie..."
                  selectedKeys={
                    selectedAcquisitionId ? [selectedAcquisitionId] : []
                  }
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    setSelectedAcquisitionId(selected || "");
                    setCurrentPage(1);
                  }}
                  isLoading={loadingAcquisitions}
                >
                  {availableAcquisitions.map((acquisition) => (
                    <SelectItem key={acquisition?.id?.toString()}>
                      {acquisition.name}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </div>

            {/* Label Filter */}
            {availableLabels.length > 0 && (
              <Select
                size="sm"
                label="Filter op label"
                placeholder="Alle labels"
                selectedKeys={selectedLabel ? [selectedLabel] : []}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string;
                  setSelectedLabel(selected || "");
                  setCurrentPage(1); // Reset to first page on label change
                }}
                startContent={
                  <Tag size={16} className="text-neutral-secondaryMid" />
                }
                classNames={{
                  trigger: "h-10",
                }}
              >
                {availableLabels.map((label) => (
                  <SelectItem key={label.label}>{label.label}</SelectItem>
                ))}
              </Select>
            )}

            {/* Context Filter Toggle */}
            {(buildingId || unitId || acquisitionId) && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-neutral-secondaryMid text-xs">
                  {useContextFilter ? (
                    <span>Gefilterd op geselecteerde context</span>
                  ) : (
                    <span>Alle afbeeldingen doorzoeken</span>
                  )}
                </div>
                <Switch
                  size="sm"
                  isSelected={useContextFilter}
                  onValueChange={(checked) => {
                    setUseContextFilter(checked);
                    setCurrentPage(1); // Reset to first page on toggle
                  }}
                >
                  <span className="text-xs">Gebruik context filter</span>
                </Switch>
              </div>
            )}

            {loading ? (
              <div className="flex h-24 items-center justify-center">
                <Spinner size="sm" />
              </div>
            ) : images.length === 0 ? (
              <div className="text-neutral-secondary-mid py-4 text-center text-sm">
                Geen afbeeldingen gevonden voor de geselecteerde entiteiten.
              </div>
            ) : (
              <>
                <div className="grid max-h-64 grid-cols-4 gap-2 overflow-y-auto p-1">
                  {images.map((image) => (
                    <div
                      key={image.id}
                      className={`relative h-20 w-full cursor-pointer overflow-hidden rounded-md bg-neutral-100 transition-all duration-200 ${isSelected(image.sharePointFileId) ? "scale-105 ring-2 ring-purple-500" : "hover:scale-105 hover:ring-1 hover:ring-neutral-300"}`}
                      onClick={() => handleImageClick(image.sharePointFileId)}
                    >
                      <img
                        src={image.imageUrl}
                        alt={`Image ${image.id}`}
                        className="h-full w-full object-cover"
                      />
                      {isSelected(image.sharePointFileId) && (
                        <div className="bg-opacity-50 absolute inset-0 flex items-center justify-center bg-purple-500">
                          <Check
                            size={20}
                            weight="fill"
                            className="text-white"
                          />
                          <div className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white">
                            {selectedImageIds.indexOf(image.sharePointFileId) +
                              1}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalImages > pageSize && (
                  <div className="border-neutral-border mt-2 flex items-center justify-between border-t pt-2">
                    <Button
                      size="sm"
                      variant="flat"
                      isIconOnly
                      isDisabled={currentPage === 1}
                      onPress={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                    >
                      <CaretLeft size={16} weight="bold" />
                    </Button>

                    <div className="flex items-center gap-2">
                      <span className="text-neutral-secondaryMid text-xs">
                        Pagina {currentPage}
                      </span>
                      <Chip size="sm" variant="flat">
                        {totalImages > currentPage * pageSize
                          ? `${images.length}+ afbeeldingen`
                          : `${totalImages} afbeeldingen`}
                      </Chip>
                    </div>

                    <Button
                      size="sm"
                      variant="flat"
                      isIconOnly
                      isDisabled={images.length < pageSize}
                      onPress={() => setCurrentPage((prev) => prev + 1)}
                    >
                      <CaretRight size={16} weight="bold" />
                    </Button>
                  </div>
                )}
              </>
            )}

            {selectedImageIds.length > 0 && (
              <Button
                size="sm"
                variant="light"
                color="danger"
                startContent={<Trash size={16} />}
                onPress={handleClearAll}
                className="mt-3 w-full"
              >
                Wis alle selecties
              </Button>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default ImageTablePicker;
